import React, { useState, useEffect } from 'react';
import { PlusCircle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import TrainingManagementContent from './TrainingManagementContent';
import TrainingCentersContent from './TrainingCentersContent';
import TrainingUsersContent from './TrainingUsersContent';
import TrainingSettings from './TrainingSettings';

const TrainingManagement = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('trainings');

  // Set active tab based on current route
  useEffect(() => {
    if (location.pathname.includes('/trainings/management/centers')) {
      setActiveTab('centers');
    } else if (location.pathname.includes('/trainings/management/users')) {
      setActiveTab('users');
    } else if (location.pathname.includes('/trainings/management/settings')) {
      setActiveTab('settings');
    } else if (location.pathname.includes('/trainings/management')) {
      setActiveTab('trainings');
    } else {
      setActiveTab('trainings'); // Default to trainings tab
    }
  }, [location.pathname]);

  // Handle tab navigation
  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'centers') {
      navigate('/trainings/management/centers');
    } else if (tabId === 'trainings') {
      navigate('/trainings/management/trainings');
    } else if (tabId === 'users') {
      navigate('/trainings/management/users');
    } else if (tabId === 'settings') {
      navigate('/trainings/management/settings');
    }
  };

  const tabs = [
    { id: 'trainings', label: 'Trainings', component: TrainingManagementContent },
    { id: 'centers', label: 'Centers', component: TrainingCentersContent },
    { id: 'users', label: 'Users', component: TrainingUsersContent },
    { id: 'settings', label: 'Settings', component: TrainingSettings }
  ];

  const activeTabData = tabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabData?.component;

  const getButtonText = () => {
    switch (activeTab) {
      case 'trainings':
        return 'Add Training';
      case 'centers':
        return 'Add Center';
      case 'users':
        return 'Add User';
      case 'settings':
        return 'Add Item';
      default:
        return 'Add';
    }
  };

  const handleAddClick = () => {
    // This will be passed down to the active component
    if (ActiveComponent) {
      // Trigger the add function in the active component
      const event = new CustomEvent('triggerAdd', { detail: { tab: activeTab } });
      window.dispatchEvent(event);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-steel-200 h-full flex flex-col">
      {/* Unified Header */}
      <div className="bg-gradient-to-r from-brand-50 to-steel-50 px-6 py-4 flex justify-between items-center border-b border-steel-200">
        <h2 className="text-2xl font-bold text-slate-900 flex items-center">
          Training Management
        </h2>
        <button
          onClick={handleAddClick}
          className="bg-brand-500 text-white px-4 py-2 rounded-xl hover:bg-brand-600 transition-colors duration-200 flex items-center font-medium shadow-lg shadow-brand-500/20"
        >
          <PlusCircle className="h-5 w-5 mr-2" />
          {getButtonText()}
        </button>
      </div>

      {/* Tabs */}
      <div className="bg-steel-50 border-b border-steel-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                activeTab === tab.id
                  ? 'border-brand-500 text-brand-500'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-steel-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {ActiveComponent && <ActiveComponent />}
      </div>
    </div>
  );
};

export default TrainingManagement;